import type { ObstacleSpawnPoint } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import { aabb, aabbFromCenterSize, type Aabb } from '../physics/Aabb';
import type { ObstacleDefinition } from './ObstacleDefinition';

/** A spawned obstacle on the track (gameplay state only). */
export class ObstacleInstance {
  /** Inactive obstacles no longer collide (e.g. cleared after a hit). */
  active = true;
  /** Colliders in track space, precomputed at spawn – obstacles are static. */
  readonly trackColliders: readonly Aabb[];
  /** Union of all colliders – cheap broad-phase test. */
  readonly bounds: Aabb;

  constructor(
    readonly uid: number,
    readonly definition: ObstacleDefinition,
    readonly segment: SegmentInstance,
    readonly spawnPoint: ObstacleSpawnPoint,
    /** Yaw of the visual only – colliders are axis-aligned boxes and do not turn. */
    readonly visualYaw = 0,
  ) {
    const origin = segment.localToTrack(spawnPoint.position);
    this.trackColliders = definition.colliders.map((c) =>
      aabbFromCenterSize({ x: origin.x + c.center.x, y: origin.y + c.center.y, z: origin.z + c.center.z }, c.size),
    );
    const b = aabb();
    b.minX = b.minY = b.minZ = Infinity;
    b.maxX = b.maxY = b.maxZ = -Infinity;
    for (const c of this.trackColliders) {
      b.minX = Math.min(b.minX, c.minX);
      b.minY = Math.min(b.minY, c.minY);
      b.minZ = Math.min(b.minZ, c.minZ);
      b.maxX = Math.max(b.maxX, c.maxX);
      b.maxY = Math.max(b.maxY, c.maxY);
      b.maxZ = Math.max(b.maxZ, c.maxZ);
    }
    this.bounds = b;
  }
}
